import { isTextBased, isThread, type ChannelDto } from '@lagerfeuer/shared';
import { MessageSquareText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { Tooltip } from '../../components/Tooltip';
import { channelsApi } from '../../lib/endpoints';
import { isId, safeId } from '../../lib/ids';
import { paths, pathForChannel } from '../../lib/routes';
import { gatewaySend } from '../../realtime/gateway';
import { setFocusedChannel } from '../../realtime/handlers';
import { useAppStore } from '../../store/app';
import { defaultChannelId } from '../../store/selectors';
import { useUi } from '../../store/ui';
import { DmHome } from '../dms/DmHome';
import { ForumView } from '../forum/ForumView';
import { MessagePane } from '../messages/MessagePane';
import { ThreadPanel } from '../threads/ThreadPanel';
import { VoiceChannelView } from '../voice';
import { ChannelHeader } from './ChannelHeader';
import { MemberList, RecipientList } from './MemberList';

/** Meldet dem Gateway den sichtbaren Kanal (unterdrückt Push für diesen Kanal, Vertrag 6.2). */
function useChannelFocus(channelId: string | null) {
  // Nach einer Neuverbindung meldet gateway.ts den Fokus nach READY neu (R7b, D27).
  useEffect(() => {
    const send = (id: string | null) => {
      setFocusedChannel(id);
      void gatewaySend('CHANNEL_FOCUS', { channelId: id });
    };
    const onVisibility = () => send(document.visibilityState === 'visible' ? channelId : null);
    send(channelId);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      send(null);
    };
  }, [channelId]);
}

/** Lädt einen unbekannten Kanal (z. B. Thread per Link) nach. */
export function useEnsureChannel(channelId: string | undefined): ChannelDto | undefined | null {
  const ch = useAppStore((s) => (channelId ? s.channels[channelId] : undefined));
  const ready = useAppStore((s) => s.ready);
  // An die ID gekoppelt: ein 404 für einen Kanal gilt nicht für den nächsten.
  const [missingId, setMissingId] = useState<string | null>(null);
  const valid = isId(channelId);
  useEffect(() => {
    if (!channelId || !valid || ch || !ready) return;
    let alive = true;
    channelsApi
      .get(channelId)
      .then((c) => alive && useAppStore.getState().dispatch('CHANNEL_CREATE', c))
      .catch(() => alive && setMissingId(channelId));
    return () => {
      alive = false;
    };
  }, [channelId, valid, ch, ready]);
  if (channelId && !valid) return null;
  if (!ch && missingId === channelId) return null;
  return ch;
}

export function ServerIndexRedirect() {
  const { serverId } = useParams();
  const exists = useAppStore((s) => !!(serverId && s.servers[serverId]));
  const target = useAppStore((s) => (serverId ? defaultChannelId(s, serverId) : null));
  if (!serverId || !exists) return <NotFoundPane text="Diesen Server gibt es nicht (mehr) oder du bist kein Mitglied." />;
  if (target) return <Navigate to={paths.channel(serverId, target)} replace />;
  return <NotFoundPane text="In diesem Server gibt es keinen sichtbaren Kanal." showNav />;
}

export function ChannelRoute() {
  const { serverId, channelId } = useParams();
  const ch = useEnsureChannel(channelId);
  if (ch === null || (serverId && serverId !== '@me' && !isId(serverId))) return <NotFoundPane text="Kanal nicht gefunden oder keine Berechtigung." showNav />;
  if (!ch) return <div className="empty"><div className="spinner" /></div>;
  // Threads öffnen im Seitenpanel des Elternkanals.
  if (isThread(ch.type) && ch.parentId) {
    const search = new URLSearchParams(window.location.search);
    return <Navigate to={pathForChannel(ch, safeId(search.get('message')) ?? undefined)} replace />;
  }
  if (ch.type === 'CATEGORY') return <ServerIndexRedirect />;
  return <ChannelView channel={ch} serverId={serverId === '@me' ? null : (serverId ?? null)} />;
}

export function DmRoute() {
  const { channelId } = useParams();
  if (!channelId) return <DmHome />;
  return <ChannelRoute />;
}

export function ChannelView({ channel, serverId }: { channel: ChannelDto; serverId: string | null }) {
  const [params, setParams] = useSearchParams();
  // Ungültige IDs (manipulierte Links) gar nicht erst an API/Gateway geben.
  const threadId = safeId(params.get('thread'));
  const jumpId = safeId(params.get('message'));
  const showMembers = useUi((s) => s.showMembers);
  const membersOpen = useUi((s) => s.membersOpen);
  const [voiceChat, setVoiceChat] = useState(false);
  useChannelFocus(threadId ?? channel.id);

  const clearParam = (key: string) => {
    const next = new URLSearchParams(params);
    next.delete(key);
    setParams(next, { replace: true });
  };

  let body: React.ReactNode;
  if (channel.type === 'VOICE' && serverId) {
    body = (
      <div className="voice-layout">
        <VoiceChannelView serverId={serverId} channelId={channel.id} />
        <Tooltip label={voiceChat ? 'Chat ausblenden' : 'Chat einblenden'}>
          <button className="icon-btn voice-chat-toggle" aria-pressed={voiceChat} onClick={() => setVoiceChat(!voiceChat)} data-testid="voice-chat-toggle">
            <MessageSquareText size={20} />
          </button>
        </Tooltip>
      </div>
    );
  } else if (channel.type === 'FORUM') {
    body = <ForumView channelId={channel.id} />;
  } else if (isTextBased(channel.type)) {
    body = <MessagePane key={channel.id} channelId={channel.id} jumpToMessageId={threadId ? null : jumpId} onJumped={() => clearParam('message')} />;
  } else {
    body = <NotFoundPane text="Dieser Kanaltyp kann nicht angezeigt werden." />;
  }

  let side: React.ReactNode = null;
  if (threadId) {
    side = (
      <ThreadPanel
        key={threadId}
        threadId={threadId}
        onClose={() => {
          const next = new URLSearchParams(params);
          next.delete('thread');
          next.delete('message');
          setParams(next);
        }}
      />
    );
  } else if (channel.type === 'VOICE' && voiceChat) {
    side = (
      <aside className="side-panel" data-testid="voice-chat-panel">
        <MessagePane channelId={channel.id} variant="panel" />
      </aside>
    );
  } else if (serverId && (showMembers || membersOpen)) {
    side = <MemberList serverId={serverId} channelId={channel.id} />;
  } else if (channel.type === 'GROUP_DM' && (showMembers || membersOpen)) {
    side = <RecipientList channelId={channel.id} />;
  }
  return (
    <div className={`channel-view ${membersOpen ? 'members-open' : ''}`} data-testid="channel-view" data-channel-id={channel.id} data-channel-type={channel.type}>
      <ChannelHeader channel={channel} serverId={serverId} />
      <div className="channel-body">
        <section className="channel-main" aria-label="Kanalinhalt">
          {body}
        </section>
        {side && <div className={`channel-side ${threadId ? 'thread' : ''}`}>{side}</div>}
        {membersOpen && <div className="drawer-scrim right" onClick={() => useUi.getState().setMembersOpen(false)} aria-hidden="true" />}
      </div>
    </div>
  );
}

export function NotFoundPane({ text, showNav }: { text: string; showNav?: boolean }) {
  return (
    <div className="empty full" data-testid="not-found">
      {showNav && (
        <button className="btn mobile-only" onClick={() => useUi.getState().setNavOpen(true)}>
          Navigation
        </button>
      )}
      <p>{text}</p>
    </div>
  );
}
